import { useMedia } from '../ui/responsive';
import { Dialog } from '../ui/overlays';
import { useEditorUI } from './ui';
import { useBoard } from './store';
import type { ReactElement } from 'react';
import { hexA } from '@scribbleplot/shared';
import { color, shadow } from '../tokens';
import { Button } from '../ui/primitives';
import type { Tool } from './store';

/**
 * Vertical tool rail.
 *
 * Order follows the design: pointer tools, then creation tools, then media.
 * Each entry carries its shortcut so the tooltip teaches the keyboard path.
 */

type ToolSpec = { tool: Tool | 'image'; label: string; key: string; icon: ReactElement };

const stroke = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.5,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
} as const;

const TOOLS: ToolSpec[][] = [
  [
    {
      tool: 'select',
      label: 'Select',
      key: 'V',
      icon: <path d="M4 2.5 L12.6 9.6 L8.8 10.2 L10.8 14 L8.9 15 L7 11.2 L4 13.6 Z" style={{ fill: 'currentColor' }} />,
    },
    {
      tool: 'hand',
      label: 'Pan',
      key: 'H (or hold Space)',
      icon: (
        <path
          d="M8 2.5v11M2.5 8h11M8 2.5 6.3 4.2M8 2.5l1.7 1.7M8 13.5l-1.7-1.7M8 13.5l1.7-1.7M2.5 8l1.7-1.7M2.5 8l1.7 1.7M13.5 8l-1.7-1.7M13.5 8l-1.7 1.7"
          style={stroke}
        />
      ),
    },
  ],
  [
    {
      tool: 'sticky',
      label: 'Sticky note',
      key: 'N',
      icon: (
        <>
          <path d="M3 3h10v6.5L9.5 13H3z" style={stroke} />
          <path d="M13 9.5H9.5V13" style={stroke} />
        </>
      ),
    },
    { tool: 'text', label: 'Text', key: 'T', icon: <path d="M3.5 4.5V3h9v1.5M8 3v10M6.5 13h3" style={stroke} /> },
    {
      tool: 'frame',
      label: 'Area / frame',
      key: 'F',
      icon: <path d="M5.5 2v12M10.5 2v12M2 5.5h12M2 10.5h12" style={{ ...stroke, strokeWidth: 1.4 }} />,
    },
    {
      tool: 'draw',
      label: 'Draw',
      key: 'D',
      icon: <path d="M3.5 12.5l.9-3L10.8 3l1.7 1.7-6.4 6.5-3 .8z" style={{ ...stroke, strokeWidth: 1.4 }} />,
    },
    {
      tool: 'connect',
      label: 'Connector',
      key: 'C',
      icon: (
        <>
          <path d="M3 12.5c3.5 0 5.5-7 9-7" style={stroke} />
          <path d="M10.2 3.6 12.9 5.4 10.4 7.4" style={stroke} />
        </>
      ),
    },
    {
      tool: 'link',
      label: 'Link card',
      key: 'L',
      icon: (
        <>
          <path d="M6.7 9.3l2.6-2.6" style={stroke} />
          <circle cx="4.9" cy="11.1" r="2.4" style={stroke} />
          <circle cx="11.1" cy="4.9" r="2.4" style={stroke} />
        </>
      ),
    },
    {
      tool: 'comment',
      label: 'Comment',
      key: 'M',
      icon: (
        <path
          d="M2.5 4.5a2 2 0 0 1 2-2h7a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2H8l-3.5 3v-3a2 2 0 0 1-2-2z"
          style={{ ...stroke, strokeWidth: 1.4 }}
        />
      ),
    },
  ],
  [
    {
      tool: 'image',
      label: 'Add image',
      key: '',
      icon: (
        <>
          <rect x="2.5" y="3" width="11" height="10" rx="1.5" style={{ ...stroke, strokeWidth: 1.4 }} />
          <circle cx="6" cy="6.8" r="1.1" style={{ fill: 'currentColor' }} />
          <path d="M4 12l3.2-3.4 2.2 2.1 1.9-1.9 2.2 2.6" style={{ ...stroke, strokeWidth: 1.4 }} />
        </>
      ),
    },
  ],
];

export function Toolbar({
  tool,
  accent,
  disabled,
  onPick,
  onPickImage,
}: {
  tool: Tool;
  accent: string;
  disabled: boolean;
  onPick: (tool: Tool) => void;
  onPickImage: () => void;
}) {
  const phone = useMedia('(max-width: 767px)');
  const { overlay, open, multiSelect, toggleMulti } = useEditorUI();
  const board = useBoard();
  if (disabled) return null;
  if (phone)
    return (
      <>
        <div className="sp-mobile-dock" role="toolbar" aria-label="Board tools">
          {(['select', 'hand', 'sticky'] as const).map((t) => {
            const spec = TOOLS.flat().find((s) => s.tool === t)!;
            return (
              <button
                key={t}
                className="sp-button"
                aria-label={spec.label}
                aria-pressed={tool === t}
                onClick={() => {
                  useEditorUI.setState({ multiSelect: false });
                  onPick(t);
                }}
              >
                <svg width="18" height="18" viewBox="0 0 16 16" aria-hidden>
                  {spec.icon}
                </svg>
                {t === 'sticky' ? 'Note' : spec.label}
              </button>
            );
          })}
          <button className="sp-button" aria-label="Add and tools" onClick={() => open('tools')}>
            <span aria-hidden style={{ fontSize: 20 }}>
              +
            </span>
            Add
          </button>
          <button className="sp-button" aria-label="Undo" disabled={!board.past.length} onClick={board.undo}>
            <span aria-hidden style={{ fontSize: 20 }}>
              ↶
            </span>
            Undo
          </button>
        </div>
        {overlay === 'tools' && (
          <Dialog title="Add and tools" drawer onClose={() => open(null)}>
            <div className="sp-tool-options">
              {TOOLS.flat()
                .filter((s) => !['select', 'hand', 'sticky'].includes(s.tool))
                .map((spec) => (
                  <button
                    key={spec.tool}
                    className="sp-button"
                    aria-label={spec.label}
                    onClick={() => {
                      open(null);
                      spec.tool === 'image' ? onPickImage() : onPick(spec.tool);
                    }}
                  >
                    <svg width="20" height="20" viewBox="0 0 16 16" aria-hidden>
                      {spec.icon}
                    </svg>
                    {spec.label}
                  </button>
                ))}
              <button
                className="sp-button"
                aria-pressed={multiSelect}
                onClick={() => {
                  toggleMulti();
                  onPick('select');
                  open(null);
                }}
              >
                Select multiple
              </button>
              <button
                className="sp-button"
                onClick={() => {
                  board.selectAll();
                  onPick('select');
                  open(null);
                }}
              >
                Select all
              </button>
              <button
                className="sp-button"
                disabled={!board.future.length}
                onClick={() => {
                  board.redo();
                  open(null);
                }}
              >
                Redo
              </button>
            </div>
          </Dialog>
        )}
      </>
    );

  return (
    <div
      role="toolbar"
      aria-label="Board tools"
      aria-orientation="vertical"
      style={{
        position: 'absolute',
        left: 12,
        top: '50%',
        transform: 'translateY(-50%)',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        padding: 6,
        background: color.panel,
        border: `1px solid ${color.lineSoft}`,
        borderRadius: 14,
        boxShadow: shadow.float,
        zIndex: 50,
      }}
    >
      {TOOLS.map((group, groupIndex) => (
        <div key={groupIndex} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {groupIndex > 0 && (
            <div aria-hidden style={{ height: 1, background: 'rgba(255,255,255,.08)', margin: '3px 4px' }} />
          )}
          {group.map((spec) => {
            const active = spec.tool === tool;
            return (
              <Button
                key={spec.tool}
                title={spec.key ? `${spec.label} — ${spec.key}` : spec.label}
                ariaLabel={spec.label}
                ariaPressed={active}
                onClick={() => (spec.tool === 'image' ? onPickImage() : onPick(spec.tool as Tool))}
                hoverStyle={{ background: color.hoverStrong }}
                style={{
                  width: 36,
                  height: 36,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 10,
                  border: 'none',
                  background: active ? hexA(accent, 0.16) : 'transparent',
                  color: active ? accent : color.icon,
                }}
              >
                <svg width={16} height={16} viewBox="0 0 16 16" aria-hidden>
                  {spec.icon}
                </svg>
              </Button>
            );
          })}
        </div>
      ))}
    </div>
  );
}
